// frontend/src/components/Search/SearchBar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAutocomplete } from '../../services/api';
import { useLanguage } from '../../hooks/useLanguage';
import AutocompleteDropdown from './AutocompleteDropdown';

interface SearchBarProps {
  placeholder?: string;
  size?: 'default' | 'large';
  className?: string;
  variant?: 'default' | 'hero';
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  size = 'default',
  className = '',
  variant = 'default',
}) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce logic: update debouncedQuery after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Fetch autocomplete results
  const { data: autocompleteData, isLoading } = useAutocomplete(
    debouncedQuery,
    showDropdown && debouncedQuery.length >= 2
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowDropdown(false);

      // Smart redirect: if there's exactly one result and it's a symptom with departments,
      // redirect directly to the department (only if there's exactly ONE department)
      if (autocompleteData?.results && autocompleteData.results.length === 1) {
        const result = autocompleteData.results[0];

        // For symptoms with exactly ONE department, go directly to that department
        if (result.type === 'symptom' && result.extra?.departments && result.extra.departments.length === 1) {
          navigate(`/departments/${result.extra.departments[0].slug}`);
          return;
        }

        // For symptoms with MULTIPLE departments, show search page so user can choose
        if (result.type === 'symptom' && result.extra?.departments && result.extra.departments.length > 1) {
          navigate(`/search?q=${encodeURIComponent(query.trim())}`);
          return;
        }

        // For other single results, go to their specific page
        if (result.type === 'department') {
          navigate(`/departments/${result.slug}`);
          return;
        }
        if (result.type === 'service') {
          navigate(`/services/${result.slug}`);
          return;
        }
        if (result.type === 'doctor') {
          navigate(`/doctors/${result.slug}`);
          return;
        }
      }

      // Default: go to search results page
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowDropdown(value.length >= 2);
  };

  const handleClear = () => {
    setQuery('');
    setDebouncedQuery('');
    setShowDropdown(false);
  };

  const handleSelectResult = () => {
    setShowDropdown(false);
    setQuery('');
    setDebouncedQuery('');
  };

  const sizeClasses = {
    default: 'h-12 text-base',
    large: 'h-14 sm:h-16 text-base sm:text-lg',
  };

  const isHero = variant === 'hero';

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <form onSubmit={handleSearch} className="w-full">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 sm:pl-5 pointer-events-none">
            <svg
              className={`w-5 h-5 sm:w-6 sm:h-6 ${isHero ? 'text-[#eeeef0]' : 'text-gray-400'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder || t('search.searchPlaceholder')}
            className={isHero ? `
              w-full ${sizeClasses[size]} pl-12 sm:pl-14 pr-4 sm:pr-5
              rounded-xl border border-[#fdfdfd]
              text-[#eeeef0] placeholder-[#eeeef0]/70 font-semibold
              outline-none transition-all duration-200
            ` : `
              w-full ${sizeClasses[size]} pl-12 sm:pl-14 pr-4 sm:pr-5
              rounded-xl border-2 border-gray-200
              bg-white text-gray-900 placeholder-gray-400
              focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
              transition-all duration-200
              dark:bg-dark-card dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-500
              shadow-sm hover:shadow-md focus:shadow-lg
            `}
            style={isHero ? { background: 'rgba(61,138,255,0.5)' } : undefined}
            aria-label="Поиск"
          />

          {query && (
            <button
              type="button"
              onClick={handleClear}
              className={`absolute inset-y-0 right-0 flex items-center pr-4 sm:pr-5 ${isHero ? 'text-[#eeeef0]/70 hover:text-[#eeeef0]' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="Очистить"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Autocomplete Dropdown */}
      {showDropdown && autocompleteData && (
        <AutocompleteDropdown
          results={autocompleteData.results}
          isLoading={isLoading}
          onSelect={handleSelectResult}
        />
      )}
    </div>
  );
};

export default SearchBar;
